import type { ExcelDownloadClient } from "app/components/React/ExcelButton/ExcelClientDownload";
import { ListVariant } from "app/pages/sales-intelligence/domain/lists/types/list";
import { matchListVariant } from "app/pages/sales-intelligence/domain/lists/utils/list-variant";
import { extractCrmAndObjectType } from "app/pages/sales-intelligence/sub-modules/opportunities/helpers/crmList";
import type { IFetchService, PostConfigObject } from "app/services/fetchService";

import { createCrmListApiService } from "./crmListApiService";
import { createStaticListsApiService } from "./staticListsApiService";
import type {
    AppsExportParams,
    FetchAppsSearchParams,
    FetchContactsParams,
    FetchLeadsSearchParams,
    LeadsExportParams,
} from "./types";

export const createOpportunityListPageApiService = (
    fetchService: IFetchService,
    excelDownloadClient: ExcelDownloadClient,
) => {
    const staticList = createStaticListsApiService(fetchService, excelDownloadClient);
    const crmList = createCrmListApiService(fetchService, excelDownloadClient);

    return {
        fetchLeadsSearchResults({ listParams, leadsType, dto }: FetchLeadsSearchParams, config?: PostConfigObject) {
            return matchListVariant(listParams, {
                [ListVariant.Static]: (id) => staticList[leadsType].fetchSearchResults(id, dto, config),
                [ListVariant.Crm]: (id) => {
                    const { crm, objectType } = extractCrmAndObjectType(id);

                    return crmList[leadsType].fetchSearchResults(crm, objectType, dto, config);
                },
            });
        },

        fetchContacts({ listParams, leadsType, dto }: FetchContactsParams, config?: PostConfigObject) {
            return matchListVariant(listParams, {
                [ListVariant.Static]: (id) => staticList[leadsType].fetchContacts(id, dto, config),
                [ListVariant.Crm]: (id) => {
                    const { crm, objectType } = extractCrmAndObjectType(id);

                    return crmList[leadsType].fetchContacts(crm, objectType, dto, config);
                },
            });
        },

        exportLeadsToExcel({ listParams, leadsType, dto }: LeadsExportParams) {
            return matchListVariant(listParams, {
                [ListVariant.Static]: (id) => staticList[leadsType].exportToExcel(id, dto),
                [ListVariant.Crm]: (id) => {
                    const { crm, objectType } = extractCrmAndObjectType(id);

                    return crmList[leadsType].exportToExcel(crm, objectType, dto);
                },
            });
        },

        fetchAppsSearchResults({ listParams, dto }: FetchAppsSearchParams, config?: PostConfigObject) {
            return matchListVariant(listParams, {
                [ListVariant.Static]: (id) => staticList.apps.fetchSearchResults(id, dto, config),
                [ListVariant.Crm]: (id) => {
                    const { crm, objectType } = extractCrmAndObjectType(id);

                    return crmList.apps.fetchSearchResults(crm, objectType, dto, config);
                },
            });
        },

        exportAppsToExcel({ listParams, dto }: AppsExportParams) {
            return matchListVariant(listParams, {
                [ListVariant.Static]: (id) => staticList.apps.exportToExcel(id, dto),
                [ListVariant.Crm]: (id) => {
                    const { crm, objectType } = extractCrmAndObjectType(id);

                    return crmList.apps.exportToExcel(crm, objectType, dto);
                },
            });
        },
    };
};
